import { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { DefaultSeo, OrganizationJsonLd } from 'next-seo';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import Loading from '@/components/Loading';
import NavBar from '@/components/NavBar';
import { SideMenu } from '@/components/SideMenu';
import { AnsweredItemsProvider } from '@/contexts/AnsweredItemsContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { CommunicationsProvider } from '@/contexts/Communications';
import { DiscountPerItemProvider } from '@/contexts/DiscountPerItemContext';
import { DrawerProvider } from '@/contexts/DrawerContext';
import { LoadingProvider } from '@/contexts/LoadingContext';
import { MergedAnsweredItemsProvider } from '@/contexts/MergedAnsweredItems';
import { MergedAnsweredItemsEnabledProvider } from '@/contexts/MergedAnsweredItemsEnabled';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { QuotationAnswerProvider } from '@/contexts/QuotationAnswerContext';
import { QuotationAnswerRenegotiationProvider } from '@/contexts/QuotationAnswersRenegotiation';
import { gtmVirtualPageView, GTM_TRACKING_ID } from '@/lib/gtm';
import createEmotionCache from '@/services/createEmotionCache';
import { pushNotification } from '@/services/notifications';
import { UserMeWebsocketUrl } from '@/services/websocket';
import { themeLight } from '@/themes/light';
import { hasRoutersHiddenSideBar } from '@/utils';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import { createGenerateClassName, StylesProvider } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-US';
import ptLocale from 'date-fns/locale/pt-BR';
import { LionessProvider } from 'lioness';
import { parseCookies } from 'nookies';
import messages from 'public/locales/messages.json';
import { defaultSeo, defaultOrganizationJson } from '../../next-seo.config';

import 'react-toastify/dist/ReactToastify.css';
import '@/styles/ReactInputVerificationCode.css';
import 'react-phone-number-input/style.css';
import 'swiper/css/bundle';
import '@/styles/globals.css';

const localeMap = {
  'pt-BR': ptLocale,
  en: enLocale,
};

const generateClassName = createGenerateClassName({
  productionPrefix: 'c',
});

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface IMyAppProps extends AppProps<IPageProps> {
  emotionCache?: EmotionCache;
}

interface IPageProps {
  page: string;
}

function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: IMyAppProps) {
  const router = useRouter();
  const { locale, pathname } = router;

  useEffect(() => {
    const { 'obraplay.token': token } = parseCookies();
    if (token) {
      const wsClient = new WebSocket(UserMeWebsocketUrl(token));
      wsClient.onopen = () => {};
      wsClient.onmessage = event => {
        const { type, payload } = JSON.parse(event.data);
        if (type === 'SERVER.NOTIFICATION.CREATED') {
          pushNotification({
            body: payload.message,
            icon: payload.category.icon,
          });
        }
      };
    }
  }, []);

  useEffect(() => {
    const mainDataLayer = {
      pageTypeName: pageProps.page || null,
      url: router.pathname,
    };

    gtmVirtualPageView(mainDataLayer);
  }, [pageProps.page, router.pathname]);
  return (
    <CacheProvider value={emotionCache}>
      <Script
        id="mdchat-index"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          var mdChatClient = "1CBB263ADD4F4577978FD51D60D27593"
          `,
        }}
      ></Script>
      <Script
        id="obraplay-hotjar"
        dangerouslySetInnerHTML={{
          __html: `
           (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:2821383,hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `,
        }}
      />
      <Head>
        <title>Obra Play</title>

        <meta
          name="application-name"
          content="Obraplay"
          key="application-name"
        />

        {/* eslint-disable-next-line @next/next/next-script-for-ga */}
        <script
          id="google-tag-manager"
          async
          dangerouslySetInnerHTML={{
            __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer', '${GTM_TRACKING_ID}');
        `,
          }}
        />
      </Head>

      <DefaultSeo {...defaultSeo} />
      <OrganizationJsonLd {...defaultOrganizationJson} />

      <AuthProvider
        initialStates={{
          isFetchingUser: !hasRoutersHiddenSideBar(router),
        }}
      >
        <StylesProvider generateClassName={generateClassName}>
          <LionessProvider messages={messages} locale={locale || 'pt-BR'}>
            <LoadingProvider>
              <DrawerProvider>
                <QuotationAnswerProvider>
                  <MergedAnsweredItemsEnabledProvider>
                    <MergedAnsweredItemsProvider>
                      <AnsweredItemsProvider>
                        <DiscountPerItemProvider>
                          <QuotationAnswerRenegotiationProvider>
                            <CommunicationsProvider>
                              <NotificationProvider>
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                  adapterLocale={localeMap['pt-BR']}
                                >
                                  <ThemeProvider theme={themeLight}>
                                    <SideMenu>
                                      <NavBar />
                                      <Component {...pageProps} />
                                    </SideMenu>
                                    <ToastContainer />
                                    <Loading
                                      withoutOverlay={pathname === '/'}
                                    />
                                  </ThemeProvider>
                                </LocalizationProvider>
                              </NotificationProvider>
                            </CommunicationsProvider>
                          </QuotationAnswerRenegotiationProvider>
                        </DiscountPerItemProvider>
                      </AnsweredItemsProvider>
                    </MergedAnsweredItemsProvider>
                  </MergedAnsweredItemsEnabledProvider>
                </QuotationAnswerProvider>
              </DrawerProvider>
            </LoadingProvider>
          </LionessProvider>
        </StylesProvider>
      </AuthProvider>
    </CacheProvider>
  );
}

export default MyApp;
